/**
 * "הקבועים שלי" — להוספה של כמה מאכלים יחד בכמויות משתנות (הצעה 1, 29.09.2026).
 *
 * **המקור הוא המועדפים שלה** (בר, 29.09.2026: "מה שיופיע בקבועים יהיה המועדפים") — במקום הרשימה
 * המנוהלת וההיסטוריה של הגרסה הראשונה. מועדף של פריט אחד = אריח עם היחידות שלו; מועדף של ארוחה
 * שלמה = אריח "מנה" שנפרש לפריטים שלו ברישום. הכמות הרגילה = מה ששמור במועדף.
 * הסדר (בר, 29.09.2026: "אם אוכלת ביצים בערב — שיופיעו קודם"): מה שהיא אוכלת בארוחה שנבחרה במסך
 * ובשעה הזו קודם, ואז לפי תדירות, ואז סדר המועדפים (החדש קודם). החלפת ארוחה במסך מסדרת מחדש.
 *
 * הלוגיקה כאן טהורה (בלי DOM ובלי רשת) — נבדקת ב-regulars.test.ts.
 */
import type { EntryItem, MealType } from '../services/entries';
import type { FoodStat } from '../services/entries';

export interface RegUnit {
  /** יחיד: "כף" */
  name: string;
  /** רבים: "כפות" */
  plural: string;
  /** משקל יחידה אחת בגרמים. 0 = "מנה" בלי משקל ידוע (הערכים הם למנה, לא ל-100 ג') */
  grams: number;
}

export interface Macros { calories: number; protein: number; carbs: number; fat: number }

export interface Regular {
  /** מפתח יציב — מזהה המועדף */
  key: string;
  /** השם המלא (כפי שנרשם ביומן) */
  name: string;
  /** השם באריח — מקוצר ("גבינה צהובה 9% שומן" ולא כל שמות היצרנים מהמאגר הלאומי) */
  label: string;
  /** שורת משנה קבועה במקום "הרגיל: …" (ארוחה: "ארוחה · 4 פריטים") */
  sub?: string;
  icon?: string;
  /** ערכים לגרם אחד; null כשאין משקל (ואז base הם הערכים למנה אחת) */
  perGram: Macros | null;
  base: Macros | null;
  units: RegUnit[];
  usual: Pick;
  /** הפריטים השמורים במועדף — מהם נבנה הרישום (מוכפלים לפי הכמות), כך שקוד מאכל / מאכל אישי נשמרים */
  tpl: EntryItem[];
}

/** הבחירה של מאכל אחד: כמות + אינדקס יחידה ב-units */
export interface Pick { qty: number; unit: number }

export const GRAM: RegUnit = { name: 'גרם', plural: 'גרם', grams: 1 };

const round1 = (n: number): number => Math.round(n * 10) / 10;

/** ½, 1½, 2 — חצאים כשבר, השאר עד ספרה אחת */
export function fmtQty(q: number): string {
  const w = Math.floor(q);
  if (Math.abs(q - w - 0.5) < 0.01) return w ? `${w}½` : '½';
  return String(round1(q));
}

export function isGram(u: RegUnit): boolean {
  return u.grams === 1 && u.name === GRAM.name;
}

/** "2 כפות", "½ כף", "30 ג'" */
export function amountText(q: number, u: RegUnit): string {
  if (isGram(u)) return `${Math.round(q)} ג'`;
  return `${fmtQty(q)} ${q <= 1 ? u.name : u.plural}`;
}

/** מעבר יחידה ששומר על אותה כמות אוכל — מעוגל לחצאים, או ל-5 ג'. בלי משקל — הכמות נשארת. */
export function convertQty(q: number, from: RegUnit, to: RegUnit): number {
  if (!from.grams || !to.grams) return q;
  const grams = q * from.grams;
  if (isGram(to)) return Math.max(5, Math.round(grams / 5) * 5);
  return Math.max(0.5, Math.round((grams / to.grams) * 2) / 2);
}

/** נגיעה נוספת באריח = עוד מנה (10 ג' בגרמים; מחצי — לאחת) */
export function stepUp(q: number, u: RegUnit): number {
  if (isGram(u)) return q + 10;
  return q < 1 ? 1 : q + 1;
}

/** − : מנה פחות; מאחת לחצי; מחצי — 0 (= הסרה) */
export function stepDown(q: number, u: RegUnit): number {
  if (isGram(u)) return Math.max(0, q - 10);
  if (q > 1) return q - 1;
  if (q > 0.5) return 0.5;
  return 0;
}

/** הערכים של בחירה */
export function pickMacros(r: Regular, p: Pick): Macros {
  const u = r.units[p.unit] ?? r.units[0];
  if (r.perGram && u.grams) {
    const g = p.qty * u.grams;
    return { calories: r.perGram.calories * g, protein: r.perGram.protein * g, carbs: r.perGram.carbs * g, fat: r.perGram.fat * g };
  }
  const b = r.base ?? { calories: 0, protein: 0, carbs: 0, fat: 0 };
  return { calories: b.calories * p.qty, protein: b.protein * p.qty, carbs: b.carbs * p.qty, fat: b.fat * p.qty };
}

/** "½ ×", "2 ×" — קידומת לתיאור כמות של פריט בארוחה שהוכפלה */
const times = (f: number): string => `${fmtQty(f)} × `;

/**
 * פריטי היומן של בחירה — אותו מבנה כמו כל הוספה אחרת. נבנים מהפריטים השמורים במועדף ומוכפלים
 * בפקטור, כך שכל שדה אחר (קוד מאכל, מאכל אישי, אייקון, יחידה) עובר כמו שהוא.
 * פריט בודד: הכמות בתיאור היא מה שנבחר ("2 כפות"). ארוחה: כל פריט נשאר רישום נפרד (מדיניות היומן).
 */
export function toEntryItems(r: Regular, p: Pick): EntryItem[] {
  const u = r.units[p.unit] ?? r.units[0];
  const tplGrams = r.tpl.length === 1 ? r.tpl[0].grams ?? 0 : 0;
  const f = r.perGram && u.grams && tplGrams ? (p.qty * u.grams) / tplGrams : p.qty;
  const single = r.tpl.length === 1;
  return r.tpl.map(({ aliases: _aliases, ...t }) => {
    const it: EntryItem = {
      ...t,
      calories: Math.round((t.calories ?? 0) * f),
      protein_g: round1((t.protein_g ?? 0) * f),
      carbs_g: round1((t.carbs_g ?? 0) * f),
      fat_g: round1((t.fat_g ?? 0) * f),
    };
    if (t.grams) it.grams = Math.round(t.grams * f);
    if (single) it.qty_desc = amountText(p.qty, u);
    else if (Math.abs(f - 1) > 0.001) it.qty_desc = t.grams ? `${it.grams} ג'` : times(f) + (t.qty_desc ?? 'מנה');
    return it;
  });
}
// ---------------------------------------------------------------- מהמועדפים

/** פריט ביומן שומר רק את שם היחידה ביחיד — רבים ליחידות הנפוצות ("3 כפות" ולא "3 כף") */
const PLURALS: Record<string, string> = {
  'כף': 'כפות', 'כפית': 'כפיות', 'כוס': 'כוסות', 'פרוסה': 'פרוסות', 'יחידה': 'יחידות', 'מנה': 'מנות',
  'חתיכה': 'חתיכות', 'פיתה': 'פיתות', 'ביצה': 'ביצים', 'גביע': 'גביעים', 'קרקר': 'קרקרים', 'פריכית': 'פריכיות',
  'מלפפון': 'מלפפונים', 'אצבע': 'אצבעות', 'חלבון': 'חלבונים', 'שפריץ': 'שפריצים',
};
const plural = (u: string): string => PLURALS[u] ?? u;

/** מה שצריך ממועדף (תת-קבוצה של Favorite — כדי שהלוגיקה תישאר טהורה, בלי תלות בשירות) */
export interface FavLike { id: string; name: string; items: EntryItem[] }

/** שם קצר לאריח: עד הפסיק הראשון ("גבינה צהובה 9% שומן, לייט, טל העמק/…" → "גבינה צהובה 9% שומן") */
export function shortName(n: string): string {
  const t = n.trim();
  const head = t.split(/[,،]/)[0].trim();
  return head.length >= 3 ? head : t;
}

/** שם לאריח של ארוחה שמורה: השם שנתנה לה, ואם הוא רק רשימת הפריטים — "פיתה + חומוס" / "פיתה ועוד 3" */
export function mealLabel(name: string, items: EntryItem[]): string {
  const n = (name ?? '').trim();
  if (n && !n.includes(',')) return n;
  const uniq = [...new Set(items.map((i) => shortName(i.name_he)))];
  return uniq.length <= 2 ? uniq.join(' + ') : `${uniq[0]} ועוד ${uniq.length - 1}`;
}

const itemMacros = (it: EntryItem): Macros =>
  ({ calories: it.calories ?? 0, protein: it.protein_g ?? 0, carbs: it.carbs_g ?? 0, fat: it.fat_g ?? 0 });

/** מועדף → קבוע. null למועדף ריק. */
export function fromFavorite(f: FavLike): Regular | null {
  const items = (f.items ?? []).filter((it) => it && typeof it.name_he === 'string');
  if (!items.length) return null;
  const key = `fav:${f.id}`;
  if (items.length > 1) {
    // ארוחה שלמה: הערכים הם למנה אחת של כל הארוחה, והכמות היא כפולות שלה
    const m = items.map(itemMacros).reduce((a, b) => ({
      calories: a.calories + b.calories, protein: a.protein + b.protein, carbs: a.carbs + b.carbs, fat: a.fat + b.fat,
    }));
    return {
      key, name: (f.name ?? '').trim() || items.map((i) => i.name_he).join(', '),
      label: mealLabel(f.name, items),
      sub: `ארוחה · ${items.length} פריטים`,
      perGram: null, base: m,
      units: [{ name: 'מנה', plural: 'מנות', grams: 0 }],
      usual: { qty: 1, unit: 0 },
      tpl: items,
    };
  }
  const it = items[0];
  const g = it.grams && it.grams > 0 ? it.grams : 0;
  const m = itemMacros(it);
  if (g) {
    const units: RegUnit[] = [];
    if (it.unit_name && it.unit_grams && it.unit_grams > 0 && it.unit_name !== GRAM.name) {
      units.push({ name: it.unit_name, plural: plural(it.unit_name), grams: it.unit_grams });
    }
    units.push(GRAM);
    // הכמות השמורה ביחידה, אם היא מתחלקת יפה לחצאים; אחרת בגרמים
    const inUnits = units.length > 1 ? g / units[0].grams : 0;
    const clean = inUnits > 0 && Math.abs(inUnits * 2 - Math.round(inUnits * 2)) < 0.05;
    return {
      key, name: it.name_he, label: shortName(it.name_he), icon: it.icon,
      perGram: { calories: m.calories / g, protein: m.protein / g, carbs: m.carbs / g, fat: m.fat / g },
      base: null,
      units,
      usual: clean ? { qty: Math.round(inUnits * 2) / 2, unit: 0 } : { qty: Math.round(g), unit: units.length - 1 },
      tpl: [it],
    };
  }
  // בלי משקל (למשל פריט מ-AI שנשמר כ"צלחת"): הערכים הם למנה, והכמות היא כפולות של אותה מנה
  const label = (it.qty_desc ?? '').trim() || 'מנה';
  return {
    key, name: it.name_he, label: shortName(it.name_he), icon: it.icon,
    perGram: null, base: m,
    units: [{ name: label, plural: label, grams: 0 }],
    usual: { qty: 1, unit: 0 },
    tpl: [it],
  };
}

const hourDist = (a: number, b: number): number => Math.min(Math.abs(a - b), 24 - Math.abs(a - b));

/**
 * הרשימה שמוצגת: כל המועדפים, מסודרים אוטומטית לפי **השעה ותדירות השימוש** (בר, 30.09.2026):
 *   ציון = 3 × (פעמים שנאכל בארוחה שנבחרה) + 2 × (פעמים שנאכל ±2ש' מהשעה הזו) + ¼ × (כל הפעמים).
 * הארוחה נבחרת לפי השעה (אפשר להחליף במסך), ולכן שני הרכיבים הראשונים הם "מה היא אוכלת עכשיו".
 * התדירות הכללית רק שוברת שוויון — כשהיא נספרה במלואה, קפה של הבוקר (17 פעמים) עמד חמישי גם בערב.
 * מאכל נספר גם לפי `aliases` — שמות שבהם הוא מופיע ביומן ("ביצה שלמה" ← "ביצים").
 * ארוחה שלמה מקבלת את הציון של הפריט הכי פחות נאכל בה (כולם נאכלים יחד). שוויון — סדר המועדפים.
 */
/** ציון השימוש של פריט (אותה נוסחה לקבועים וללשונית "מועדפים" בחיפוש) — ראו buildRegulars */
function useScorer(stats: FoodStat[], nowHour: number, meal?: MealType): (it: EntryItem) => number {
  const byName = new Map(stats.map((s) => [s.name, s]));
  return (it) => {
    let total = 0;
    for (const name of new Set([it.name_he, ...(it.aliases ?? [])])) {
      const s = byName.get(name);
      if (!s) continue;
      const near = s.hours.filter((h) => hourDist(h, nowHour) <= 2).length;
      const inMeal = meal ? (s.meals ?? []).filter((m) => m === meal).length : 0;
      total += 3 * inMeal + 2 * near + 0.25 * s.count;
    }
    return total;
  };
}

/**
 * לשונית "מועדפים" בחיפוש — באותו סדר אוטומטי כמו הקבועים (בר, 30.09.2026: "כן" לשאלה אם גם שם). היה לפי
 * תאריך שמירה. ארוחה שמורה — לפי הפריט הכי פחות נאכל בה; שוויון — הסדר המקורי (החדש קודם). בלי סינון כפילויות.
 */
export function orderFavorites<T extends FavLike>(favs: T[], stats: FoodStat[], nowHour: number, meal?: MealType): T[] {
  const score = useScorer(stats, nowHour, meal);
  return favs
    .map((f, i) => ({ f, i, s: (f.items ?? []).length ? Math.min(...f.items.map(score)) : 0 }))
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .map((x) => x.f);
}

export function buildRegulars(favs: FavLike[], stats: FoodStat[], nowHour: number, meal?: MealType): Regular[] {
  const score = useScorer(stats, nowHour, meal);
  const seen = new Set<string>();
  const out: Array<{ r: Regular; s: number; i: number }> = [];
  favs.forEach((f, i) => {
    const r = fromFavorite(f);
    if (!r) return;
    // אותו מאכל שסומן פעמיים (למשל מעמוד המאכל ומשמירת ארוחה) — אריח אחד, הראשון (החדש)
    const sig = r.tpl.map((t) => t.name_he).join('|');
    if (seen.has(sig)) return;
    seen.add(sig);
    out.push({ r, s: Math.min(...r.tpl.map(score)), i });
  });
  return out.sort((a, b) => b.s - a.s || a.i - b.i).map((x) => x.r);
}
